import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import Swal from "sweetalert2";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import { AuthContext } from "../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../utils/api";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import PhoneInput from "react-phone-input-2";
import { userCountry } from "../../../../utils/getUserCountry";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageInstructors = forwardRef((props, ref) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [instructors, setInstructors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newInstructor, setNewInstructor] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    role: ["instructor"],
    instituteId: user?.instituteId || "",
    isPublishResultAllowed: false,
    locked: false,
  });
  const [editInstructor, setEditInstructor] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    isPublishResultAllowed: false,
    locked: false,
  });
  const [initialEditInstructor, setInitialEditInstructor] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    isPublishResultAllowed: false,
    locked: false,
  });
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  // Bulk publish access states
  const [selectedInstructorIds, setSelectedInstructorIds] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const columns = [
    { key: "firstName", label: "First Name", sortable: true },
    { key: "lastName", label: "Last Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "phoneNumber", label: "Phone Number", sortable: true },
    { key: "isPublishResultAllowed", label: "Publish Result Access", sortable: false, filter: true },
    { key: "locked", label: "Locked", sortable: false, filter: true },
    { key: "edit", label: "", sortable: false },
    { key: "delete", label: "", sortable: false },
  ];

  // Expose openModal method to parent via ref
  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true),
  }));

  // Fetch Instructors from API
  const fetchInstructors = useCallback(async () => {
    if (!user?.instituteId) {
      setErrors({ fetch: "No institute ID found." });
      setLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/get-all/${user.instituteId}/instructor`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch instructors");
      const data = await response.json();

      const filteredUsers = data.filter(
        (inst) =>
          inst.role[0] === "instructor" && inst.instituteId === user.instituteId
      );

      setInstructors(filteredUsers);
      setErrors({});
    } catch (err) {
      setErrors({ fetch: err.message });
      swalError(err.message || "Failed to load instructors.");
    } finally {
      setLoading(false);
    }
  }, [user?.instituteId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchInstructors();
    }
  }, [fetchInstructors, isAuthenticated]);

  // Validate the creation form
  const validateCreateForm = () => {
    const newErrors = {};
    if (!newInstructor.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!newInstructor.lastName.trim())
      newErrors.lastName = "Last name is required";
    if (!newInstructor.email.trim()) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(newInstructor.email))
      newErrors.email = "Email is invalid";
    if (!newInstructor.password) newErrors.password = "Password is required";
    if (newInstructor.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    if (newInstructor.password !== newInstructor.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate the edit form
  const validateEditForm = () => {
    const newErrors = {};
    if (!editInstructor.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!editInstructor.lastName.trim())
      newErrors.lastName = "Last name is required";
    if (!editInstructor.email.trim()) newErrors.email = "Email is required";
    if (!/\S+@\S+\.\S+/.test(editInstructor.email))
      newErrors.email = "Email is invalid";
    if (editInstructor.password && editInstructor.password.length < 8)
      newErrors.password = "Password must be at least 8 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes for creation form
  const handleCreateInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // component supports checkbox inputs
    if (type === "checkbox") {
      setNewInstructor((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }
    setNewInstructor((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Check if edit form has changed
  const checkEditChanged = useCallback(
    (updated) => {
      return (
        updated.firstName !== initialEditInstructor.firstName ||
        updated.lastName !== initialEditInstructor.lastName ||
        updated.email !== initialEditInstructor.email ||
        updated.phoneNumber !== initialEditInstructor.phoneNumber ||
        updated.password !== initialEditInstructor.password ||
        updated.isPublishResultAllowed !==
          initialEditInstructor.isPublishResultAllowed ||
        updated.locked !== initialEditInstructor.locked
      );
    },
    [initialEditInstructor]
  );

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setEditInstructor((prev) => {
      const updated = { ...prev, [name]: newValue };
      setIsEditChanged(checkEditChanged(updated));
      return updated;
    });
  };

  // Handle Create Instructor
  const handleCreateInstructor = async (e) => {
    e.preventDefault();
    if (!validateCreateForm()) return;
    setIsCreating(true); // Start loading
    try {
      const response = await fetchWithAuth(`${serverUrl}/api/user/add`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: newInstructor.firstName,
          lastName: newInstructor.lastName,
          email: newInstructor.email,
          phoneNumber: newInstructor.phoneNumber,
          isPublishResultAllowed: newInstructor.isPublishResultAllowed,
          locked: newInstructor.locked,
          password: newInstructor.password,
          role: newInstructor.role,
          instituteId: newInstructor.instituteId,
        }),
      });
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create instructor");
      }

      const createdInstructor = await response.json();
      setInstructors([...instructors, createdInstructor]);
      setIsModalOpen(false);
      setNewInstructor({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: ["instructor"],
        instituteId: user.instituteId,
        isPublishResultAllowed: false,
        locked: false,
      });
      swalSuccess("Instructor created successfully");
    } catch (err) {
      swalError(err.message || "Failed to create instructor.");
    } finally {
      setIsCreating(false); // Stop loading
    }
  };

  // Handle Edit Instructor
  const handleEditInstructor = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    setIsUpdating(true); // Start loading
    try {
      const requestBody = {
        firstName: editInstructor.firstName,
        lastName: editInstructor.lastName,
        email: editInstructor.email,
        phoneNumber: editInstructor.phoneNumber,
        isPublishResultAllowed: editInstructor.isPublishResultAllowed,
        locked: editInstructor.locked,
      };

      if (editInstructor.password) {
        requestBody.password = editInstructor.password;
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/user/update/${editInstructor.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update instructor");
      }

      const updatedInstructor = await response.json();
      setInstructors(
        instructors.map((inst) =>
          inst._id === updatedInstructor._id ? updatedInstructor : inst
        )
      );
      setIsEditModalOpen(false);
      setShowEditPassword(false);
      setEditInstructor({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        isPublishResultAllowed: false,
        locked: false,
      });
      setInitialEditInstructor({
        id: "",
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        isPublishResultAllowed: false,
        locked: false,
      });
      setIsEditChanged(false);
      swalSuccess("Instructor updated successfully");
    } catch (err) {
      swalError(err.message || "Failed to update instructor.");
    } finally {
      setIsUpdating(false); // Stop loading
    }
  };

  // Handle row selection for Instructors table
  const handleRowSelect = (e, instructorId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedInstructorIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(instructorId)) {
        newSet.delete(instructorId);
      } else {
        newSet.add(instructorId);
      }
      return newSet;
    });
  };

  // Handle select all for Instructors table
  const handleSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedInstructorIds((prev) => {
      const newSet = new Set(prev);
      if (areAllVisibleSelected) {
        visibleItemIds.forEach((id) => newSet.delete(id));
      } else {
        visibleItemIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handle Bulk Publish Result Access
  const handleBulkPublishAccess = async () => {
    if (selectedInstructorIds.size === 0) return;

    // Show action-choice dialog
    const swalResult = await swalConfirm(
      "Publish Result Access",
      `Choose an action for the ${selectedInstructorIds.size} selected instructor${selectedInstructorIds.size > 1 ? "s" : ""}:`,
      "Grant Access",
      "Remove Access",
      true
    );

    if (!swalResult) {
      return;
    }

    let giveAccess = false;
    if (swalResult.isConfirmed) {
      giveAccess = true;
    } else if (swalResult.dismiss === "cancel") {
      giveAccess = false;
    } else {
      return;
    }

    setIsBulkUpdating(true);

    const selectedInstructors = instructors.filter((inst) =>
      selectedInstructorIds.has(inst._id)
    );
    const successRecords = [];
    const errorRecords = [];

    const promises = selectedInstructors.map(async (instructor) => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/user/update/${instructor._id}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ isPublishResultAllowed: giveAccess }),
          }
        );
        if (response && response.ok) {
          const updated = await response.json();
          return { instructor: updated, success: true };
        }
        const errorData = await response.json().catch(() => ({}));
        return {
          instructor,
          success: false,
          error: errorData.message || "Failed to update",
        };
      } catch (error) {
        return {
          instructor,
          success: false,
          error: error.message || "Network error",
        };
      }
    });

    try {
      const results = await Promise.all(promises);

      results.forEach((res) => {
        if (res.success) {
          successRecords.push(res.instructor);
        } else {
          errorRecords.push(res.instructor);
        }
      });

      // Update local state for successfully updated instructors
      setInstructors((prev) =>
        prev.map((inst) => {
          const updated = successRecords.find((s) => s._id === inst._id);
          return updated ? updated : inst;
        })
      );
      setSelectedInstructorIds(new Set());

      const accessLabel = giveAccess ? "Access Given" : "Access Removed";
      const successColor = giveAccess ? "#d4edda" : "#d1ecf1";
      const successBorder = giveAccess ? "#c3e6cb" : "#bee5eb";
      const successText = giveAccess ? "#155724" : "#0c5460";

      const successRowsHtml = successRecords
        .map(
          (inst) =>
            `<tr style="background: ${successColor};">
          <td style="padding: 8px; border: 1px solid ${successBorder}; color: ${successText};">✓</td>
          <td style="padding: 8px; border: 1px solid ${successBorder};">${inst.firstName} ${inst.lastName}</td>
          <td style="padding: 8px; border: 1px solid ${successBorder}; color: ${successText}; font-weight: 600;">${accessLabel}</td>
        </tr>`
        )
        .join("");

      const errorRowsHtml = errorRecords
        .map(
          (inst) =>
            `<tr style="background: #f8d7da;">
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">✗</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb;">${inst.firstName} ${inst.lastName}</td>
          <td style="padding: 8px; border: 1px solid #f5c6cb; color: #721c24;">Failed</td>
        </tr>`
        )
        .join("");

      const tableHtml = `
        <div style="max-height: 400px; overflow: auto; margin-top: 10px; -webkit-overflow-scrolling: touch;">
          <div style="min-width: 500px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 0.85em; white-space: nowrap;">
              <thead>
                <tr style="background: #f8f9fa; position: sticky; top: 0;">
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center; width: 40px;"></th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Instructor Name</th>
                  <th style="padding: 10px 8px; border: 1px solid #dee2e6; text-align: center;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${successRowsHtml}
                ${errorRowsHtml}
              </tbody>
            </table>
          </div>
        </div>
        <div style="margin-top: 12px; display: flex; gap: 20px; justify-content: center; flex-wrap: wrap;">
          <span style="color: #155724; font-weight: 600;">✓ Updated: ${successRecords.length}</span>
          <span style="color: #721c24; font-weight: 600;">✗ Failed: ${errorRecords.length}</span>
        </div>
      `;

      const icon = errorRecords.length === 0 ? "success" : successRecords.length === 0 ? "error" : "info";
      const title = errorRecords.length === 0
        ? `Publish Result Access ${giveAccess ? "Granted" : "Removed"}`
        : successRecords.length === 0
        ? "Update Failed"
        : "Update Summary";

      Swal.fire({
        icon,
        title,
        html: tableHtml,
        width: "90%",
        confirmButtonText: "Got it!",
      });
    } catch (err) {
      swalError("An error occurred during the update.");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Handle Delete Instructor
  const handleDelete = async (instructorId) => {
    const result = await swalConfirm(
      "Are you sure?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel"
    );

    if (!result.isConfirmed) return;

    setIsDeleting(true); // Start deleting loader/state
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/user/delete/${instructorId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete instructor");
      }

      setInstructors(instructors.filter((inst) => inst._id !== instructorId));
      swalSuccess("Instructor deleted successfully");
    } catch (err) {
      swalError(err.message || "Failed to delete instructor.");
    } finally {
      setIsDeleting(false); // Stop deleting loader/state
    }
  };

  // Enhance Instructors Data with Action Buttons
  const enhancedInstructors = instructors.map((inst) => ({
    ...inst,
    isPublishResultAllowed: inst.isPublishResultAllowed ? "Yes" : "No",
    locked: inst.locked ? "Yes" : "No",
    edit: (
      <button
        className="edit-btn"
        onClick={() => {
          setEditInstructor({
            id: inst._id,
            firstName: inst.firstName,
            lastName: inst.lastName,
            email: inst.email,
            phoneNumber: inst.phoneNumber,
            password: "",
            isPublishResultAllowed: inst.isPublishResultAllowed || false,
            locked: inst.locked || false,
          });
          setInitialEditInstructor({
            id: inst._id,
            firstName: inst.firstName,
            lastName: inst.lastName,
            email: inst.email,
            phoneNumber: inst.phoneNumber,
            password: "",
            isPublishResultAllowed: inst.isPublishResultAllowed || false,
            locked: inst.locked || false,
          });
          setShowEditPassword(false);
          setIsEditModalOpen(true);
          setIsEditChanged(false);
        }}
        disabled={isDeleting || isUpdating || isCreating} // Disable if any operation is in progress
      >
        <i className="fa-solid fa-pen-to-square"></i>
      </button>
    ),
    delete: (
      <button
        className="delete-btn"
        onClick={() => handleDelete(inst._id)}
        disabled={isDeleting || isUpdating || isCreating} // Disable if any operation is in progress
      >
        <i className="fa-solid fa-trash-can"></i>
      </button>
    ),
  }));

  return (
    <>
      <div className="manage-container">
        <div className="header">
          <button
            onClick={handleBulkPublishAccess}
            className="btn-export"
            style={{
              backgroundColor: "#6366f1",
              color: "white",
              border: "none",
              marginRight: "0.5rem",
            }}
            disabled={isBulkUpdating || selectedInstructorIds.size === 0}
            title={
              selectedInstructorIds.size === 0
                ? "Select instructors to manage publish result access"
                : `Manage publish result access for ${selectedInstructorIds.size} instructor(s)`
            }
          >
            {isBulkUpdating ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <i className="fa-solid fa-unlock-keyhole"></i>
            )}
            &nbsp; Publish Result Access ({selectedInstructorIds.size})
          </button>
          <button className="create-btn" onClick={() => setIsModalOpen(true)}>
            Add Instructor +
          </button>
        </div>
        {loading && <Loader style={{ fontSize: "3rem" }} />}
        {errors.fetch && <div className="error-message">{errors.fetch}</div>}
        {!loading && !errors.fetch && (
          <Table
            data={enhancedInstructors}
            columns={columns}
            caption="Instructors"
            isCheckBoxAvailable={true}
            selectedRowIds={selectedInstructorIds}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAll}
            disableRowClickSelection={true}
          />
        )}
      </div>
      {/* Add Instructor Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          if (!isCreating) {
            setIsModalOpen(false);
            setErrors({});
          }
        }}
      >
        <h2>Add New Instructor</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Fill in the details below to add a new instructor to your institute.
        </p>
        <form onSubmit={handleCreateInstructor} className="instructor-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={newInstructor.firstName}
                placeholder="Enter first name"
                onChange={handleCreateInputChange}
                required
                disabled={isCreating}
              />
              {errors.firstName && (
                <span className="error">{errors.firstName}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={newInstructor.lastName}
                placeholder="Enter last name"
                onChange={handleCreateInputChange}
                required
                disabled={isCreating}
              />
              {errors.lastName && (
                <span className="error">{errors.lastName}</span>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Email
            </label>
            <input
              type="email"
              name="email"
              value={newInstructor.email}
              placeholder="Enter instructor's email"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <PhoneInput
              placeholder="Enter Phone Number"
              country={userCountry}
              value={newInstructor.phoneNumber}
              onChange={(value) => {
                setNewInstructor((prev) => ({
                  ...prev,
                  phoneNumber: value,
                }));
              }}
              inputStyle={{
                height: "45px",
                fontSize: "16px",
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                height: "45px",
              }}
              containerStyle={{
                width: "100%",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              specialLabel="Enter Phone Number"
              dropdownClass="phone-input-dropdown"
              dropdownStyle={{
                maxHeight: "200px",
                overflowX: "hidden",
              }}
              disabled={isCreating}
            />
          </div>
          <div className="form-group password-field">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={newInstructor.password}
              placeholder="Create a password (min 8 characters)"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
            <span
              className="toggle-password"
              onClick={() => !isCreating && setShowPassword((prev) => !prev)}
              style={{
                cursor: isCreating ? "default" : "pointer",
                marginLeft: "5px",
              }}
            >
              <i
                className={`fa-regular ${
                  showPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </span>
            {errors.password && (
              <span className="error">{errors.password}</span>
            )}
          </div>
          <div className="form-group password-field">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Confirm
              Password
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={newInstructor.confirmPassword}
              placeholder="Re-enter password"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
            <span
              className="toggle-password"
              onClick={() =>
                !isCreating && setShowConfirmPassword((prev) => !prev)
              }
              style={{
                cursor: isCreating ? "default" : "pointer",
                marginLeft: "5px",
              }}
            >
              <i
                className={`fa-regular ${
                  showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                }`}
              />
            </span>
            {errors.confirmPassword && (
              <span className="error">{errors.confirmPassword}</span>
            )}
          </div>
          <div className="form-group">
            <label>Allow Publish Result</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="isPublishResultAllowed"
                checked={newInstructor.isPublishResultAllowed}
                onChange={handleCreateInputChange}
                disabled={isCreating}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {newInstructor.isPublishResultAllowed ? "Allowed" : "Not Allowed"}
              </span>
            </label>
            <small className="helper-text" style={{ color: "#666" }}>
              Toggle to{" "}
              {newInstructor.isPublishResultAllowed
                ? "disallow"
                : "allow"}{" "}
              the instructor to publish results.
            </small>
          </div>
          <div className="form-group">
            <label>Account Locked</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="locked"
                checked={newInstructor.locked}
                onChange={handleCreateInputChange}
                disabled={isCreating}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {newInstructor.locked ? "Locked" : "Active"}
              </span>
            </label>
            <small className="helper-text" style={{ color: "#666" }}>
              Toggle to {newInstructor.locked ? "unblock" : "block"} the
              instructor's account.
            </small>
          </div>
          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Instructor
            </button>
          )}
        </form>
      </Modal>
      {/* Edit Instructor Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isUpdating) {
            setIsEditModalOpen(false);
            setErrors({});
            setShowEditPassword(false);
          }
        }}
      >
        <h2>Edit Instructor</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the instructor information.
        </p>
        <form onSubmit={handleEditInstructor} className="instructor-form">
          <div className="form-row">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={editInstructor.firstName}
                placeholder="Enter first name"
                onChange={handleEditInputChange}
                required
                disabled={isUpdating}
              />
              {errors.firstName && (
                <span className="error">{errors.firstName}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={editInstructor.lastName}
                placeholder="Enter last name"
                onChange={handleEditInputChange}
                required
                disabled={isUpdating}
              />
              {errors.lastName && (
                <span className="error">{errors.lastName}</span>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Email
            </label>
            <input
              type="email"
              name="email"
              value={editInstructor.email}
              placeholder="Enter instructor's email"
              onChange={handleEditInputChange}
              required
              disabled={isUpdating}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <PhoneInput
              placeholder="Enter Phone Number"
              country={userCountry}
              value={editInstructor.phoneNumber}
              onChange={(value) => {
                setEditInstructor((prev) => {
                  const updated = { ...prev, phoneNumber: value };
                  setIsEditChanged(checkEditChanged(updated));
                  return updated;
                });
              }}
              inputStyle={{
                height: "45px",
                fontSize: "16px",
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                height: "45px",
              }}
              containerStyle={{
                width: "100%",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              specialLabel="Enter Phone Number"
              dropdownClass="phone-input-dropdown"
              dropdownStyle={{
                maxHeight: "200px",
                overflowX: "hidden",
              }}
              disabled={isUpdating}
            />
          </div>
          {/* Change Password Section */}
          <div
            className="form-group"
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "10px",
              padding: "0.85rem 1rem",
              background: "#fafafa",
              transition: "all 0.3s ease",
            }}
          >
            <label
              style={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
              }}
            >
              <i className="fa-solid fa-key" style={{ color: "#f59e0b" }}></i>
              Change Password
            </label>
            <div
              style={{
                display: "flex",
                gap: "0.5rem",
                marginTop: "0.5rem",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={showEditPassword ? "text" : "password"}
                  name="password"
                  value={editInstructor.password}
                  onChange={handleEditInputChange}
                  placeholder="Enter new password (min. 8 chars)"
                  disabled={isUpdating}
                  autoComplete="new-password"
                  style={{ width: "100%", paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#888",
                    padding: "4px",
                    fontSize: "14px",
                  }}
                  title={showEditPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <i
                    className={`fa-solid ${
                      showEditPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
              {errors.password && (
                <small
                  style={{
                    color: "#ef4444",
                    fontSize: "0.75rem",
                    marginTop: "0.25rem",
                    display: "block",
                  }}
                >
                  {errors.password}
                </small>
              )}
            </div>
          </div>
          <div className="form-group">
            <label>Allow Publish Result</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="isPublishResultAllowed"
                checked={editInstructor.isPublishResultAllowed}
                onChange={handleEditInputChange}
                disabled={isUpdating}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editInstructor.isPublishResultAllowed ? "Allowed" : "Not Allowed"}
              </span>
            </label>
            <small className="helper-text" style={{ color: "#666" }}>
              Toggle to{" "}
              {editInstructor.isPublishResultAllowed
                ? "disallow"
                : "allow"}{" "}
              the instructor to publish results.
            </small>
          </div>
          <div className="form-group">
            <label>Account Locked</label>
            <label className="solution-toggle">
              <input
                type="checkbox"
                name="locked"
                checked={editInstructor.locked}
                onChange={handleEditInputChange}
                disabled={isUpdating}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editInstructor.locked ? "Locked" : "Active"}
              </span>
            </label>
            <small className="helper-text" style={{ color: "#666" }}>
              Toggle to {editInstructor.locked ? "unblock" : "block"} the
              instructor's account.
            </small>
          </div>
          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No changes detected.");
                }
              }}
            >
              Update Instructor
            </button>
          )}
        </form>
      </Modal>
    </>
  );
});

export default ManageInstructors;
